import { CHEMINS_PUBLICS } from './liensWeb';
import { SUR_LE_WEB } from '../theme/tokens';

/**
 * DESTINATION DEMANDÉE AVANT LA CONNEXION — pour y revenir une fois connecté.
 *
 * LE PROBLÈME. Arriver sur `/profil/informations` sans session ne peut pas marcher : `RootNavigator`
 * ne MONTE PAS les écrans protégés tant que `session` est nul, donc react-navigation ne trouve
 * rien à résoudre et retombe sur l'onboarding. Sans mémoire de l'adresse demandée, un lien partagé
 * ou un favori envoie l'utilisateur se connecter puis l'abandonne sur l'écran d'accueil — la
 * panne silencieuse qu'on ferme partout ailleurs.
 *
 * POURQUOI `sessionStorage` ET NON UNE VARIABLE. La connexion Google QUITTE la page : le
 * navigateur part chez Google et revient sur `/auth/callback`. Tout ce qui vivait en mémoire est
 * perdu entre les deux. `sessionStorage` survit à ce trajet, reste cantonné à l'onglet, et
 * disparaît à sa fermeture. On n'y écrit qu'un CHEMIN — jamais un jeton, jamais une donnée.
 *
 * POURQUOI À L'IMPORT. La capture doit précéder le premier rendu : dès que react-navigation
 * applique sa configuration de liens, il réécrit `window.location` vers ce qu'il a su résoudre, et
 * l'adresse demandée est déjà perdue. Ce module est importé par `RootNavigator`, donc évalué avant
 * que le conteneur ne soit monté.
 */
const CLE = 'hormeia:destination-demandee';

function lireCheminCourant(): string | null {
  if (!SUR_LE_WEB) return null;
  const { pathname, search } = window.location;
  if (CHEMINS_PUBLICS.includes(pathname)) return null;
  return pathname + search;
}

/** Sans effet hors du web, et silencieux si le stockage est refusé (navigation privée, réglages). */
function memoriser(chemin: string | null): void {
  if (!chemin) return;
  try {
    window.sessionStorage.setItem(CLE, chemin);
  } catch {
    // Pas de stockage → pas de retour à la destination. Dégradation acceptable : la connexion
    // fonctionne, elle atterrit simplement sur l'écran par défaut.
  }
}

// Capture au chargement du module, avant tout rendu (voir plus haut).
memoriser(lireCheminCourant());

/**
 * Y a-t-il une destination en attente ? CONSULTE SANS CONSOMMER — c'est ce qui permet d'envoyer la
 * personne vers la connexion tout en gardant l'adresse pour l'y ramener ensuite.
 */
export function destinationEnAttente(): boolean {
  if (!SUR_LE_WEB) return false;
  try {
    return window.sessionStorage.getItem(CLE) !== null;
  } catch {
    return false;
  }
}

/**
 * Rend la destination mémorisée et l'oublie — un seul usage, pour qu'une navigation ultérieure ne
 * la rejoue pas. `null` s'il n'y en a pas, ou si elle désigne la page déjà affichée.
 */
export function prendreDestination(): string | null {
  if (!SUR_LE_WEB) return null;
  try {
    const chemin = window.sessionStorage.getItem(CLE);
    if (chemin) window.sessionStorage.removeItem(CLE);
    if (!chemin) return null;
    // Déjà arrivé — cas d'une personne déjà connectée qui ouvre directement l'adresse : la
    // navigation l'a résolue toute seule, rejouer ne ferait que remonter l'écran pour rien.
    if (chemin === window.location.pathname + window.location.search) return null;
    return chemin;
  } catch {
    return null;
  }
}
